import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { takeOverTicket } from '../api/easeedesk'
import { closeIncidentFalsePositive, incidentQuery, type SecuriteeIncidentRow } from '../api/securitee'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { ticketOwnerHtml } from './columns'
import { IncidentNis2 } from './IncidentNis2'

/**
 * Tichetul incidentului in panoul Securitee > Incidents: numarul si starea
 * (legatura spre Easeedesk), omul care il lucreaza, "Take" - preluarea din
 * Easeedesk, cu aceeasi intrebare cand tichetul are deja om - si "False
 * positive", care muta si tichetul in starea din Dispatching (sursa XDR).
 */
export function IncidentTicketBox({ incident, canTake = false, canFalsePositive = false }: { incident: SecuriteeIncidentRow; canTake?: boolean; canFalsePositive?: boolean }) {
  const client = useQueryClient()
  const ticket = Number(incident.ticket_id)
  const [asking, setAsking] = useState(false)
  const [closing, setClosing] = useState(false)
  const fpStatus = incident.fp_ticket_status ?? ''
  const closeFalse = useMutation({
    mutationFn: () => closeIncidentFalsePositive(incident.ID),
    onSuccess: (r) => {
      setClosing(false)
      const moved = r.ticket_status ? ` Ticket #${ticket} went to ${r.ticket_status}.` : ` Ticket #${ticket} stays open.`
      toast(`Closed as a false positive.${moved} Undo it from the row for ${r.undo_minutes} minutes.`, 'Incident', 'success')
      void client.invalidateQueries({ queryKey: ['securitee', 'incidents'] })
      void client.invalidateQueries({ queryKey: incidentQuery(incident.ID).queryKey })
    },
    onError: (e: Error) => toast(e.message, 'Incident', 'error'),
  })
  // numai panoul se reciteste: o recitire a listei ar inchide randul desfacut
  const take = useMutation({
    mutationFn: (force: boolean) => takeOverTicket(ticket, force),
    onSuccess: (r) => {
      if (r.confirm === 1) {
        setAsking(true)
        return
      }
      setAsking(false)
      toast(`Ticket #${ticket} is yours.`, 'Ticket', 'success')
      void client.invalidateQueries({ queryKey: incidentQuery(incident.ID).queryKey })
    },
    onError: (e: Error) => toast(e.message, 'Ticket', 'error'),
  })

  return (
    <span className="incident-ticket-box">
      <a href={`/app/easeedesk/ticket/${ticket}`}>
        Ticket #{ticket} {incident.ticket_status}
      </a>
      <span dangerouslySetInnerHTML={{ __html: ticketOwnerHtml(incident.ticket_assignee ?? '', incident.ticket_open === true) }} />
      {canTake && (
        <button type="button" className="btn btn-xs btn-primary incident-take" disabled={take.isPending} onClick={() => take.mutate(false)}>
          Take
        </button>
      )}
      {canFalsePositive && incident.ticket_open === true && incident.false_positive !== true && (
        <button type="button" className="btn btn-xs btn-default incident-false-positive" onClick={() => setClosing(true)}>
          False positive
        </button>
      )}
      {ticket > 0 && <IncidentNis2 ticket={ticket} firstDetectedUtc={incident.first_detected_at} />}
      <Modal
        id="incident-panel-fp"
        open={closing}
        onClose={() => setClosing(false)}
        title="Close as a false positive?"
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setClosing(false)}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" id="incident-panel-fp-confirm" disabled={closeFalse.isPending} onClick={() => closeFalse.mutate()}>
              Yes, false positive
            </button>
          </>
        }
      >
        <p>
          <strong>{incident.title}</strong> on <strong>{incident.device_name || 'the device'}</strong> leaves the Open list.{' '}
          {fpStatus ? `Ticket #${ticket} goes to ${fpStatus} and gets a comment:` : `Ticket #${ticket} stays open: close it in Easeedesk.`}
        </p>
        {fpStatus && (
          <p className="incident-fp-comment">
            Closed as a false positive from Securitee. Incident #{incident.ID}: {incident.title} on {incident.device_name || `device #${incident.device_id}`}.
          </p>
        )}
        <p className="text-muted">You can undo it from the row for a few minutes, if nobody changes the ticket meanwhile.</p>
      </Modal>
      <Modal
        id="incident-take"
        open={asking}
        onClose={() => setAsking(false)}
        title={`Take ticket #${ticket}?`}
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setAsking(false)}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" id="incident-take-confirm" disabled={take.isPending} onClick={() => take.mutate(true)}>
              Yes, take it
            </button>
          </>
        }
      >
        <p>
          Ticket #{ticket} is {incident.ticket_assignee || 'someone else'}'s. Take it anyway? The change shows in the ticket history.
        </p>
      </Modal>
    </span>
  )
}
